import { useCallback, useEffect, useState } from 'react';

import { nameAvatar } from '@/utils';

interface UseRoundedImageOptions {
  alt?: string;
  fallback?: string;
  size: number;
  url?: string;
  dtName?: string;
}

const ProfilePicItem = ({
  alt = '',
  className = '',
  dtName,
  fallback,
  size,
  url,
}: UseRoundedImageOptions & { className?: string }) => {
  const [src, setSrc] = useState(url);

  useEffect(() => {
    setSrc(url);
  }, [url]);

  if (src) {
    return (
      <img
        alt={alt}
        className={`rounded-full object-cover shrink-0 ${className}`.trim()}
        height={size}
        src={src}
        width={size}
        onError={() => (fallback ? setSrc(fallback) : setSrc(undefined))}
      />
    );
  }

  if (!dtName) return null;

  return (
    <div
      className={`rounded-full flex justify-center items-center bg-gdt-gray-5 font-extrabold uppercase ${className}`}
      style={{
        height: size,
        width: size,
      }}
    >
      <p>{nameAvatar(dtName)}</p>
    </div>
  );
};

export const useUsersProfilePic = ({
  alt = '',
  fallback = '',
  size = 40,
  url,
  dtName,
}: Partial<UseRoundedImageOptions> = {}) => {
  const [params, setParams] = useState<UseRoundedImageOptions>({
    alt,
    fallback,
    size,
    url,
    dtName,
  });

  const setImageParams = useCallback(
    (next: Partial<UseRoundedImageOptions>) => {
      setParams((prev) => ({ ...prev, ...next }));
    },
    []
  );

  // key={params.url} fuerza remount cuando cambia la url via setImageParams,
  // reiniciando el estado interno de src en ProfilePicItem
  const renderImage = useCallback(
    (className = '') => (
      <ProfilePicItem key={params.url} {...params} className={className} />
    ),
    [params]
  );

  const setProfilePic = useCallback(
    (opts: UseRoundedImageOptions, className = '') => (
      <ProfilePicItem {...opts} className={className} />
    ),
    []
  );

  return { renderImage, setImageParams, setProfilePic };
};
